import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import AnnualTaxDocumentService from './AnnualTaxDocumentService';

/**
 * Once-a-year tax paperwork for everyone payroll withheld tax from.
 *
 * Deliberately its own page rather than part of Salary Payment: that page is
 * visited three times a month to move money, this one once a year to produce
 * documents, and mixing a yearly job into a monthly one buries both.
 *
 * The two employment types need different documents, which is the whole reason
 * this page exists:
 *   company employee — ONE certificate summarising the year, issued here;
 *   freelancer       — already certified per payment, so what they need is the
 *                      year collected into a single file to file their return from.
 */

const baht = (value) =>
  `฿${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const errorText = (error) => error?.response?.data?.message || error?.message || 'Something went wrong';

// The tax year is the calendar year money moved in, so December's 5th run — paid
// in January — belongs to the year after the payroll month it came from.
const yearOptions = () => {
  const now = new Date().getFullYear();
  return [now, now - 1, now - 2, now - 3, now - 4];
};

const AnnualTaxDocuments = () => {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyAdminId, setBusyAdminId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AnnualTaxDocumentService.getAnnualTaxDocuments(year);
      setReport(response.data);
    } catch (error) {
      toast.error(errorText(error));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const issue = async (row) => {
    setBusyAdminId(row.adminId);
    try {
      const { data } = await AnnualTaxDocumentService.issueAnnualCertificate(row.adminId, year);
      toast.success(data.created
        ? `Issued ${data.certificateNo} — ${baht(data.taxWithheld)} withheld`
        : `${data.certificateNo} was already issued for ${year}`);
      await load();
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setBusyAdminId(null);
    }
  };

  const openDocument = async (row) => {
    setBusyAdminId(row.adminId);
    try {
      if (row.employmentType === 'parttime') {
        await AnnualTaxDocumentService.openAnnualBundlePdf(row.adminId, year);
      } else {
        await AnnualTaxDocumentService.openAnnualCertificatePdf(row.adminId, year);
      }
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setBusyAdminId(null);
    }
  };

  const rows = report?.rows || [];

  return (
    <div className="p-6">
      <div className="flex items-center gap-4 flex-wrap">
        <h1 className="text-xl font-bold text-gray-900">เอกสารภาษีประจำปี</h1>
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="px-3 py-1.5 rounded-md border-0 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
        >
          {yearOptions().map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>
        {loading && <span className="text-sm text-gray-500">loading…</span>}
      </div>

      <p className="mt-2 text-xs text-gray-500 max-w-3xl">
        The tax year is the calendar year the money actually moved, so December&apos;s 5th run —
        paid in January — counts in the following year. Only runs marked paid are included.
      </p>

      {report && (
        <div className="mt-4 flex gap-3 flex-wrap text-sm">
          <div className="px-3 py-1.5 rounded border border-gray-200 bg-white">
            <span className="text-gray-500">Paid in {report.year}: </span>
            <span className="font-semibold">{baht(report.grossTotal)}</span>
          </div>
          <div className="px-3 py-1.5 rounded border border-amber-200 bg-amber-50">
            <span className="text-gray-500">Withheld: </span>
            <span className="font-semibold text-amber-800">{baht(report.whtTotal)}</span>
          </div>
        </div>
      )}

      <div className="mt-4 bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-2 px-4 text-left text-xs font-medium text-gray-700 uppercase">Admin</th>
              <th className="py-2 px-4 text-right text-xs font-medium text-gray-700 uppercase">Payments</th>
              <th className="py-2 px-4 text-right text-xs font-medium text-gray-700 uppercase">Paid</th>
              <th className="py-2 px-4 text-right text-xs font-medium text-gray-700 uppercase">Withheld</th>
              <th className="py-2 px-4 text-left text-xs font-medium text-gray-700 uppercase">Document</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row) => {
              const isPartTime = row.employmentType === 'parttime';
              const isBusy = busyAdminId === row.adminId;

              return (
                <tr key={row.adminId} className="hover:bg-gray-50">
                  <td className="py-2 px-4 text-sm font-medium text-gray-900">
                    {row.adminName}
                    {isPartTime && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-800">part-time</span>
                    )}
                    {!row.isActive && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">left</span>
                    )}
                  </td>
                  <td className="py-2 px-4 text-sm text-right text-gray-600">{row.payments}</td>
                  <td className="py-2 px-4 text-sm text-right">{baht(row.gross)}</td>
                  <td className="py-2 px-4 text-sm text-right font-semibold text-amber-800">
                    {baht(row.withholdingTax)}
                  </td>
                  <td className="py-2 px-4 text-xs">
                    {isPartTime ? (
                      // Already certified per payment: what is missing is the year
                      // in one file, not another certificate.
                      <button
                        onClick={() => openDocument(row)}
                        disabled={isBusy}
                        className="px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-800 disabled:opacity-50"
                      >
                        {isBusy ? 'opening…' : 'ชุดเอกสารทั้งปี'}
                      </button>
                    ) : row.certificateNo ? (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">{row.certificateNo}</span>
                        <button
                          onClick={() => openDocument(row)}
                          disabled={isBusy}
                          className="px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-800 disabled:opacity-50"
                        >
                          {isBusy ? 'opening…' : '50 ทวิ'}
                        </button>
                      </div>
                    ) : row.hasPayee ? (
                      <button
                        onClick={() => issue(row)}
                        disabled={isBusy}
                        className="px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-800 disabled:opacity-50"
                      >
                        {isBusy ? 'issuing…' : 'ออก 50 ทวิ'}
                      </button>
                    ) : (
                      // The document needs a tax id and address, which live on the
                      // payee record — so say what to fix rather than failing later.
                      <span className="text-red-600">
                        link a WHT payee on the admin record first
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 px-4 text-sm text-center text-gray-500">
                  Nothing was withheld from anyone in {year}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AnnualTaxDocuments;
